<script>
import fileSize from 'filesize'
import uploadcare from 'uploadcare-widget/uploadcare.min.js'
import Unsplash from './unsplash'

export default {
  name: 'ImageUploader',
  props: {
    value: {
      type: String,
      default: null,
    },
    crop: {
      type: String,
      default: null,
    },
    dimensionsMin: {
      type: Array,
      default: null,
    },
    sizeMax: {
      type: Number,
      default: null,
    },
    placeholder: {
      type: String,
      default: '',
    },
    image: {
      type: [Boolean, String],
      default: false,
    },
    theme: {
      type: String,
      default: 'input',
    },
    favorites: {
      type: Array,
      default: () => [],
    },
    clear: {
      type: Boolean,
      default: true,
    },
    buttonType: {
      type: String,
      default: 'primary',
      validator: v => !v || /primary|default/.test(v),
    },
    buttonPosition: {
      type: String,
      default: 'right',
      validator: v => !v || /right|left/.test(v),
    },
  },
  data() {
    return {
      credentials: {
        publicKey: process.env.VUE_APP_UPLOADCARE_PUBLIC_KEY,
      },
      preview: null,
      size: null,
      loading: false,
    }
  },
  computed: {
    tabs() {
      return 'file unsplash url camera dropbox gdrive box skydrive'
    },
    options() {
      const { crop, tabs } = this
      const { publicKey } = this.credentials

      return {
        publicKey,
        tabs,
        crop,
        validators: [
          this.checkDimensions,
          this.checkSize,
        ],
      }
    },
    showInnerPreview() {
      return this.preview && !this.$slots.preview && !this.$scopedSlots.preview
    },
    defaultPlaceholder() {
      const { crop } = this
      const props = []

      crop && props.push(crop)

      return props.join(', ')
    },
  },
  watch: {
    preview(val) {
      this.getSize(val)
    },
  },
  async created() {
    const { image, value } = this

    if (typeof image === 'string' && image.length) {
      this.preview = image
      this.loading = true
    } else {
      this.preview = value
    }

    uploadcare.registerTab('unsplash', this.unsplashTab())
  },
  methods: {
    onClick() {
      this.fileGroup = uploadcare.openDialog(null, this.options)

      this.fileGroup.done(filePromise => {
        this.loading = true
        filePromise.done(this.onSuccessUpload)
        filePromise.fail(err => {
          this.$emit('error', err)
          this.loading = false
        })
      })

      this.fileGroup.fail(err => this.$emit('error', err))
    },
    async checkDimensions({ originalImageInfo }) {
      if (
        !originalImageInfo ||
        !this.dimensionsMin || this.dimensionsMin.length < 2
      ) {
        return
      }

      const { width, height } = originalImageInfo

      if (width < this.dimensionsMin[0] || height < this.dimensionsMin[1]) {
        this.fileGroup && this.fileGroup.reject()
        await this.$nextTick()
        this.$notify({
          type: 'error',
          title: 'Error',
          message: `Minimum file dimensions ${this.dimensionsMin[0]}x${this.dimensionsMin[1]} px`,
        })
        throw new Error('dimensions')
      }
    },
    async checkSize({ size }) {
      if (!size || !this.sizeMax) {
        return
      }

      const sizeMax = this.sizeMax * 1024

      if (sizeMax < size) {
        this.fileGroup && this.fileGroup.reject()
        await this.$nextTick()
        this.$notify({
          type: 'error',
          title: 'Error',
          message: `Maximum file size: ${fileSize(sizeMax)}.
          Your file size is ${fileSize(size)}`,
        })
        throw new Error('size')
      }
    },
    onSuccessUpload(image) {
      const { uuid, crop } = image

      let cropStr = ''
      if (crop) {
        const { left, top, width, height } = crop
        cropStr = `-/crop/${width}x${height}/${left},${top}/`
      }

      const cdnUrl = `//ucarecdn.com/${uuid}/${cropStr}`

      image.cdnUrl = cdnUrl
      this.preview = cdnUrl
      this.$emit('onUpload', image)
      this.loading = false
    },
    unsplashTab() {
      return function([container], [button], dialogApi, settings) {
        const gallery = new Unsplash({
          propsData: {
            dialogApi,
            settings,
          },
        })
        const icon = button.querySelector('.uploadcare--icon > use')

        container.appendChild(gallery.$mount().$el)

        icon.setAttribute('xlink:href', '#uploadcare--icon-unsplash')
        button.title = 'Unsplash'
      }
    },
    onClear() {
      this.$emit('onUpload', { cdnUrl: '' })
      this.preview = null
    },
    getSize(image) {
      const xhr = new XMLHttpRequest()
      xhr.open('HEAD', image, true)
      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4 && xhr.status === 200) {
          this.size = xhr.getResponseHeader('Content-Length')
        }
      }
      xhr.send(null)
    },
  },
}
</script>

<template>
  <div
    :class="$style.container"
    @load.capture="loading = false"
  >
    <div
      v-loading="loading"
      :class="$style.innerContainer"
      @click.prevent="onClick"
    >
      <div
        :class="[
          $style.inputWrapper,
          buttonPosition === 'left' && $style.inputWrapperLeft
        ]"
      >
        <div
          :class="[
            $style.uploader,
            $style[`uploader--${theme}`],
            'image-uploader__container'
          ]"
        >
          <img
            v-if="showInnerPreview"
            :class="$style.uploaderPreview"
            :src="preview"
            :alt="preview"
            :title="preview"
          >
          <span
            v-else
            :class="[
              $style.uploaderTip,
              'image-uploader__tip'
            ]"
          >
            {{ placeholder || defaultPlaceholder }}
          </span>
        </div>
        <el-button
          :type="buttonType"
          plain
          :class="$style.uploaderButton"
        >
          <slot name="button-text">
            Select file
          </slot>
        </el-button>
      </div>
      <button
        v-if="clear && preview"
        :class="$style.clear"
        @click.stop="onClear"
      />
    </div>
    <slot
      v-if="preview"
      name="preview"
      :preview="preview"
      :size="size"
    />
  </div>
</template>

<style lang="scss" module>
.container {
  width: 100%;
}

.innerContainer,
.inputWrapper {
  z-index: 0; // fix clear button z-index
  display: flex;
  width: 100%;
  height: 100%;
}

.inputWrapper {
  cursor: pointer;

  &:not(.inputWrapperLeft) {
    .uploader {
      &--input {
        border-right: none;
        border-radius: rem(4px 0 0 4px);
      }
    }

    .uploaderButton {
      border-radius: rem(0 4px 4px 0);
    }
  }

  &.inputWrapperLeft {
    .uploader {
      &--input {
        order: 2;
        border-left: none;
        border-radius: rem(0 4px 4px 0);
      }
    }

    .uploaderButton {
      border-radius: rem(4px 0 0 4px);
    }
  }
}

.uploader {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin: 0;

  &--input {
    min-height: rem(42px);
    padding: rem(0 15px);
    border: rem(1px) solid #dcdfe6;
  }
}

.uploaderPreview {
  max-width: rem(160px);
  max-height: rem(60px);
  margin: rem(15px 0);
}

.uploaderButton {
  min-height: 100%;
}

.uploaderTip {
  font-size: rem(14px);
  color: #c0c4cc;
}

.clear {
  position: absolute;
  top: 1px;
  right: 1px;
  z-index: 1;
  width: rem(18px);
  height: rem(18px);
  padding: 0;
  cursor: pointer;
  background: white;
  border: none;
  border-radius: rem(0 4px 0 4px);
  outline: none;
  opacity: 0.3;

  &:before,
  &:after {
    position: absolute;
    top: rem(2px);
    left: rem(8px);
    width: 2px;
    height: rem(14px);
    content: '';
    background-color: #333;
  }

  &:before {
    transform: rotate(45deg);
  }

  &:after {
    transform: rotate(-45deg);
  }

  &:hover {
    opacity: 1;
  }
}
</style>
